import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, formatDateTime, toNum } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { MoneyInput } from "@/components/MoneyInput";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Plus, Boxes, ArrowUp, ArrowLeftRight, Trash2, Pencil, History, Users, Wallet } from "lucide-react";

const APP_TYPES = ["Pulsa & Data", "Token Listrik", "PPOB", "E-Wallet", "Bank", "BPJS", "Multifinance", "Lainnya"];

const DEFAULT_TRANSFER_FEES = [
  { method: "Sesama Bank", fee: 0 },
  { method: "BI Fast", fee: 2500 },
  { method: "Transfer Online", fee: 6500 },
  { method: "Virtual Account", fee: 0 },
  { method: "SKN / LLG", fee: 3000 },
];

// Kategori metode default (Transfer Bank + E-Wallet). Tiap kategori punya sub-metode + potongan.
const DEFAULT_FEE_CATEGORIES = [
  { name: "Transfer Bank", admin_no_potongan: true, methods: [
    { method: "Sesama", fee: 0 },
    { method: "BI Fast", fee: 2500 },
    { method: "Transfer Online", fee: 6500 },
    { method: "Virtual Account", fee: 0 },
    { method: "SKN/LLG", fee: 3000 },
  ] },
  { name: "E-Wallet", admin_no_potongan: false, methods: [
    { method: "GoPay", fee: 2000 },
    { method: "DANA", fee: 500 },
  ] },
];

export default function Apps() {
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openTopup, setOpenTopup] = useState(params.get("topup") === "1");
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openHistory, setOpenHistory] = useState(null);
  const [openAccounts, setOpenAccounts] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/apps").then((r) => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (a) => {
    if (!(await confirmDialog({ title: `Hapus aplikasi ${a.name}?` }))) return;
    await api.delete(`/apps/${a.id}`);
    toast.success("Aplikasi dihapus"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Master Aplikasi</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Aplikasi & Saldo</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-transfer-balance" variant="outline" onClick={() => setOpenTransfer(true)} className="gap-2">
            <ArrowLeftRight className="h-4 w-4" /> Transfer Saldo
          </Button>
          <Button data-testid="btn-new-app" onClick={() => { setEditing(null); setOpenForm(true); }} className="gap-2">
            <Plus className="h-4 w-4" /> Aplikasi Baru
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center">
          <Boxes className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">Belum ada aplikasi. Tambahkan aplikasi pembayaran pertama Anda.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((a) => (
            <Card key={a.id} data-testid={`app-card-${a.id}`} className="p-4 card-hover">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center font-bold text-sm">
                      {a.name[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.type}</p>
                    </div>
                  </div>
                  <p className="mt-3 money text-xl font-bold tracking-tight">{rupiah(a.current_balance)}</p>
                  {a.account_number && <p className="mt-0.5 text-xs text-muted-foreground">{a.account_number}</p>}
                  {a.accounts?.length > 0 && (
                    <div className="mt-2 space-y-0.5" data-testid={`app-accounts-${a.id}`}>
                      {a.accounts.map((ac) => (
                        <div key={ac.id} className="flex justify-between gap-2 text-[11px]">
                          <span className="text-muted-foreground truncate">• {ac.label}</span>
                          <span className="money shrink-0">{rupiah(ac.saldo || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className={`h-2 w-2 rounded-full mt-2 shrink-0 ${a.status === "active" ? "bg-emerald-500" : "bg-slate-400"}`} />
              </div>
              <div className="mt-3 grid grid-cols-4 gap-1.5">
                <Button data-testid={`btn-topup-${a.id}`} size="sm" variant="outline" onClick={() => { setEditing(a); setOpenTopup(true); }} className="gap-1 text-xs px-2">
                  <ArrowUp className="h-3 w-3" /> Topup
                </Button>
                <Button data-testid={`btn-history-${a.id}`} size="sm" variant="outline" onClick={() => setOpenHistory(a)} className="gap-1 text-xs px-2">
                  <History className="h-3 w-3" />
                </Button>
                <Button data-testid={`btn-edit-${a.id}`} size="sm" variant="outline" onClick={() => { setEditing(a); setOpenForm(true); }} className="gap-1 text-xs px-2">
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button data-testid={`btn-delete-${a.id}`} size="sm" variant="outline" onClick={() => del(a)} className="gap-1 text-xs px-2 hover:bg-destructive hover:text-destructive-foreground">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <Button data-testid={`btn-accounts-${a.id}`} size="sm" variant="ghost" onClick={() => setOpenAccounts(a.id)} className="mt-1.5 w-full gap-1.5 text-xs">
                <Users className="h-3.5 w-3.5" /> Kelola Akun{a.accounts?.length ? ` (${a.accounts.length})` : ""}
              </Button>
            </Card>
          ))}
        </div>
      )}

      <AppForm open={openForm} onClose={() => { setOpenForm(false); setEditing(null); }} editing={editing} onSaved={load} />
      <TopupDialog open={openTopup} onClose={() => setOpenTopup(false)} apps={list} initial={editing} onSaved={load} />
      <TransferDialog open={openTransfer} onClose={() => setOpenTransfer(false)} apps={list} onSaved={load} />
      <HistoryDialog app={openHistory} onClose={() => setOpenHistory(null)} />
      <AccountsDialog app={list.find((x) => x.id === openAccounts) || null} onClose={() => setOpenAccounts(null)} onSaved={load} />
    </div>
  );
}

function AppForm({ open, onClose, editing, onSaved }) {
  const empty = { name: "", type: "PPOB", notes: "", status: "active" };
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    setForm(editing || empty);
    const src = editing?.fee_categories?.length
      ? editing.fee_categories
      : (editing ? [] : DEFAULT_FEE_CATEGORIES);
    setCats(src.map((c) => ({ name: c.name, admin_no_potongan: c.admin_no_potongan ?? (String(c.name || "").trim().toLowerCase() !== "e-wallet"), methods: (c.methods || []).map((m) => ({ ...m, no_potongan: m.no_potongan ?? (String(m.method || "").trim().toLowerCase().includes("sesama")) })) })));
    /* eslint-disable-next-line */
  }, [editing, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Operasi kategori metode
  const setCatName = (ci, v) => setCats((arr) => arr.map((c, i) => (i === ci ? { ...c, name: v } : c)));
  const setCatFlag = (ci, v) => setCats((arr) => arr.map((c, i) => (i === ci ? { ...c, admin_no_potongan: v } : c)));
  const addCat = () => setCats((arr) => [...arr, { name: "", admin_no_potongan: true, methods: [] }]);
  const removeCat = (ci) => setCats((arr) => arr.filter((_, i) => i !== ci));
  const addMethod = (ci) => setCats((arr) => arr.map((c, i) => (i === ci ? { ...c, methods: [...c.methods, { method: "", fee: "", no_potongan: false }] } : c)));
  const setMethod = (ci, mi, k, v) => setCats((arr) => arr.map((c, i) => (i === ci ? { ...c, methods: c.methods.map((m, j) => (j === mi ? { ...m, [k]: v } : m)) } : c)));
  const removeMethod = (ci, mi) => setCats((arr) => arr.map((c, i) => (i === ci ? { ...c, methods: c.methods.filter((_, j) => j !== mi) } : c)));

  const save = async () => {
    if (!form.name) return toast.error("Isi nama aplikasi");
    const fee_categories = cats
      .filter((c) => (c.name || "").trim())
      .map((c) => ({
        name: c.name.trim(),
        admin_no_potongan: !!c.admin_no_potongan,
        methods: (c.methods || [])
          .filter((m) => (m.method || "").trim())
          .map((m) => ({ method: m.method.trim(), fee: toNum(m.fee), no_potongan: !!m.no_potongan })),
      }));
    try {
      if (editing?.id) {
        await api.patch(`/apps/${editing.id}`, { ...form, fee_categories });
      } else {
        await api.post("/apps", { ...form, fee_categories });
      }
      toast.success("Tersimpan"); onSaved?.(); onClose();
    } catch { /* handled by interceptor */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit Aplikasi" : "Aplikasi Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Aplikasi</Label><Input data-testid="app-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div>
            <Label>Jenis</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="app-type"><SelectValue /></SelectTrigger>
              <SelectContent>{APP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Catatan</Label><Textarea data-testid="app-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          {!editing && (
            <p className="text-[11px] text-muted-foreground -mt-1">Saldo & akun diatur setelah aplikasi dibuat, lewat tombol <b>Kelola Akun</b> di kartu aplikasi.</p>
          )}

          {/* Kategori Metode (per aplikasi) — mis. Transfer Bank, E-Wallet, dsb */}
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Kategori Metode & Potongan</Label>
                <p className="text-[11px] text-muted-foreground">Dipakai saat Kirim. Kategori boleh punya sub-metode (mis. GoPay/DANA) atau tanpa sub (tanpa potongan).</p>
              </div>
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCats(DEFAULT_FEE_CATEGORIES.map((c) => ({ name: c.name, admin_no_potongan: c.admin_no_potongan, methods: c.methods.map((m) => ({ ...m })) })))}>
                Reset default
              </Button>
            </div>

            <div className="space-y-3">
              {cats.map((c, ci) => (
                <div key={ci} className="rounded-md border bg-muted/30 p-2.5 space-y-2" data-testid={`app-cat-${ci}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      className="flex-1 h-9 font-medium"
                      placeholder="Nama kategori (mis. E-Wallet)"
                      data-testid={`app-cat-name-${ci}`}
                      value={c.name}
                      onChange={(e) => setCatName(ci, e.target.value)}
                    />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeCat(ci)} data-testid={`app-cat-remove-${ci}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-md bg-background/60 px-2.5 py-1.5">
                    <span className="text-[11px] text-muted-foreground leading-tight">Biaya admin pakai tarif <b>TANPA potongan</b> (walau ada potongan)</span>
                    <Switch data-testid={`app-cat-nopot-${ci}`} checked={!!c.admin_no_potongan} onCheckedChange={(v) => setCatFlag(ci, v)} />
                  </div>
                  <div className="space-y-2 pl-2 border-l-2 border-border/60">
                    {c.methods.length === 0 && (
                      <p className="text-[11px] text-muted-foreground italic">Tanpa sub-metode (tanpa potongan)</p>
                    )}
                    {c.methods.map((m, mi) => (
                      <div key={mi} className="space-y-1" data-testid={`app-cat-${ci}-method-${mi}`}>
                        <div className="flex items-center gap-2">
                          <Input
                            className="flex-1 h-9"
                            placeholder="Nama metode (mis. GoPay)"
                            data-testid={`app-cat-${ci}-method-name-${mi}`}
                            value={m.method}
                            onChange={(e) => setMethod(ci, mi, "method", e.target.value)}
                          />
                          <div className="w-32">
                            <MoneyInput
                              data-testid={`app-cat-${ci}-method-fee-${mi}`}
                              value={m.fee}
                              onChange={(v) => setMethod(ci, mi, "fee", v)}
                            />
                          </div>
                          <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeMethod(ci, mi)} data-testid={`app-cat-${ci}-method-remove-${mi}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <label className="flex items-center gap-2 pl-0.5 cursor-pointer">
                          <Switch data-testid={`app-cat-${ci}-method-nopot-${mi}`} checked={!!m.no_potongan} onCheckedChange={(v) => setMethod(ci, mi, "no_potongan", v)} />
                          <span className="text-[11px] text-muted-foreground leading-tight">Selalu tarif <b>NORMAL</b> (tanpa potongan) — mis. Sesama</span>
                        </label>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={() => addMethod(ci)} data-testid={`app-cat-${ci}-method-add`}>
                      <Plus className="h-3 w-3" /> Tambah metode
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="gap-1 text-xs w-full" onClick={addCat} data-testid="app-cat-add">
              <Plus className="h-3 w-3" /> Tambah kategori
            </Button>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-app" onClick={save} className="flex-1">Simpan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TopupDialog({ open, onClose, apps, initial, onSaved }) {
  const [appId, setAppId] = useState(initial?.id || "");
  const [kind, setKind] = useState("topup");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => { setAppId(initial?.id || ""); setAmount(""); setNotes(""); }, [initial, open]);

  const save = async () => {
    if (!appId) return toast.error("Pilih aplikasi");
    if (!amount) return toast.error("Isi nominal");
    await api.post(`/apps/${appId}/balance`, { kind, amount: toNum(amount), notes });
    toast.success("Saldo diperbarui"); onSaved?.(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Tambah / Kurangi Saldo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Aplikasi</Label>
            <Select value={appId} onValueChange={setAppId}>
              <SelectTrigger data-testid="topup-app"><SelectValue placeholder="Pilih aplikasi" /></SelectTrigger>
              <SelectContent>{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {rupiah(a.current_balance)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipe</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="topup-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="topup">Topup (+)</SelectItem>
                <SelectItem value="deduct">Kurangi (-)</SelectItem>
                <SelectItem value="adjustment">Penyesuaian (± nominal langsung)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nominal</Label><MoneyInput data-testid="topup-amount" value={amount} onChange={setAmount} /></div>
          <div><Label>Catatan</Label><Input data-testid="topup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-topup" onClick={save} className="flex-1">Simpan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onClose, apps, onSaved }) {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [amount, setAmount] = useState(""); const [notes, setNotes] = useState("");
  useEffect(() => { setFrom(""); setTo(""); setAmount(""); setNotes(""); }, [open]);
  const save = async () => {
    if (!from || !to || !amount) return toast.error("Lengkapi field");
    await api.post("/apps/transfer", { from_app_id: from, to_app_id: to, amount: toNum(amount), notes });
    toast.success("Transfer sukses"); onSaved?.(); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Transfer Antar Aplikasi</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Dari</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Aplikasi asal" /></SelectTrigger>
              <SelectContent>{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {rupiah(a.current_balance)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Ke</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Aplikasi tujuan" /></SelectTrigger>
              <SelectContent>{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Nominal</Label><MoneyInput data-testid="transfer-amount" value={amount} onChange={setAmount} /></div>
          <div><Label>Catatan</Label><Input data-testid="transfer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-transfer" onClick={save} className="flex-1">Transfer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ app, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!app) return;
    api.get(`/apps/${app.id}/history`, { params: { limit: 30 } }).then((r) => setRows(r.data));
  }, [app]);
  return (
    <Dialog open={!!app} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Riwayat: {app?.name}</DialogTitle></DialogHeader>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Belum ada riwayat</p>
        ) : (
          <div className="divide-y">
            {rows.map((r) => (
              <div key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{r.kind}{r.account_label ? ` · ${r.account_label}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(r.created_at)} · {r.notes || "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`money text-sm font-semibold ${r.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.amount >= 0 ? "+" : ""}{rupiah(r.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground money">saldo: {rupiah(r.balance_after)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// Kelola akun di dalam sebuah aplikasi (multi-akun).
function AccountsDialog({ app, onClose, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [accNo, setAccNo] = useState("");
  const [opening, setOpening] = useState("");
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [op, setOp] = useState(null); // {accId, label, kind}
  const [opAmount, setOpAmount] = useState("");
  const [opNotes, setOpNotes] = useState("");

  useEffect(() => {
    // reset form saat dialog dibuka/ganti app
    setAdding(false); setLabel(""); setAccNo(""); setOpening(""); setEditId(null);
    setOp(null); setOpAmount(""); setOpNotes("");
  }, [app?.id]);

  if (!app) return null;
  const accounts = app.accounts || [];
  const total = accounts.reduce((s, x) => s + (x.saldo || 0), 0);

  const startEdit = (ac) => { setEditId(ac.id); setLabel(ac.label || ""); setAccNo(ac.account_number || ""); setOpening(""); setAdding(true); };
  const startAdd = () => { setEditId(null); setLabel(""); setAccNo(""); setOpening(""); setAdding(true); };

  const saveAccount = async () => {
    if (!label.trim()) return toast.error("Isi nama akun");
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/apps/${app.id}/accounts/${editId}`, { label: label.trim(), account_number: accNo || null });
        toast.success("Akun diperbarui ✓");
      } else {
        await api.post(`/apps/${app.id}/accounts`, { label: label.trim(), account_number: accNo || null, opening_balance: toNum(opening) });
        toast.success("Akun ditambahkan ✓");
      }
      setAdding(false); setEditId(null); setLabel(""); setAccNo(""); setOpening("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan akun");
    } finally { setBusy(false); }
  };

  const delAccount = async (ac) => {
    if (!(await confirmDialog({ title: `Hapus akun "${ac.label}"?`, description: `Saldo Rp ${(ac.saldo || 0).toLocaleString("id-ID")} akan ikut terhapus dari total aplikasi.` }))) return;
    try {
      await api.delete(`/apps/${app.id}/accounts/${ac.id}`);
      toast.success("Akun dihapus ✓");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const openOp = (accId, label, kind, current) => {
    setOp({ accId, label, kind, current: current || 0 });
    setOpAmount(kind === "set" ? String(Math.round(current || 0)) : "");
    setOpNotes("");
  };

  const saveOp = async () => {
    if (opAmount === "" || opAmount == null) return toast.error("Isi nominal");
    setBusy(true);
    try {
      let kind = op.kind;
      let amount = toNum(opAmount);
      if (op.kind === "set") {
        kind = "adjustment";
        amount = toNum(opAmount) - (op.current || 0); // selisih untuk mencapai nilai baru
      }
      const account_id = op.accId === "__umum__" ? null : op.accId;
      await api.post(`/apps/${app.id}/balance`, { kind, amount, notes: opNotes || null, account_id });
      toast.success(op.kind === "topup" ? "Topup berhasil ✓" : op.kind === "deduct" ? "Saldo dikurangi ✓" : "Saldo diperbarui ✓");
      setOp(null); setOpAmount(""); setOpNotes("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const renderOp = (id) => (op?.accId === id ? (
    <div className="mt-2 rounded-md border p-2.5 bg-background space-y-2" data-testid={`acc-op-${id}`}>
      <p className="text-xs font-medium">
        {op.kind === "topup" ? "Topup" : op.kind === "deduct" ? "Kurangi" : "Atur saldo (nilai baru)"} — {op.label}
      </p>
      <MoneyInput data-testid="acc-op-amount" value={opAmount} onChange={setOpAmount} />
      <Input data-testid="acc-op-notes" placeholder="Catatan (opsional)" value={opNotes} onChange={(e) => setOpNotes(e.target.value)} />
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" className="flex-1" onClick={() => setOp(null)}>Batal</Button>
        <Button size="sm" className="flex-1" data-testid="acc-op-save" disabled={busy} onClick={saveOp}>Simpan</Button>
      </div>
    </div>
  ) : null);

  return (
    <Dialog open={!!app} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="accounts-dialog">
        <DialogHeader><DialogTitle>Akun: {app.name}</DialogTitle></DialogHeader>

        <div className="rounded-lg border p-3 bg-accent/30 flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Total saldo aplikasi (jumlah semua akun)</p>
            <p className="money text-lg font-bold">{rupiah(total)}</p>
          </div>
          <p className="text-xs text-muted-foreground">{accounts.length} akun</p>
        </div>

        <div className="space-y-2">
          {accounts.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground py-3 text-center">Belum ada akun. Klik tombol Tambah Akun untuk membuat akun beserta saldonya.</p>
          )}
          {accounts.map((ac) => (
            <div key={ac.id} className="rounded-lg border p-3" data-testid={`account-row-${ac.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-primary" />{ac.label}</p>
                  {ac.account_number && <p className="text-xs text-muted-foreground">{ac.account_number}</p>}
                </div>
                <p className="money font-bold shrink-0">{rupiah(ac.saldo || 0)}</p>
              </div>
              <div className="mt-2 grid grid-cols-5 gap-1.5">
                <Button size="sm" variant="outline" className="text-xs gap-1 px-1" data-testid={`acc-topup-${ac.id}`} onClick={() => openOp(ac.id, ac.label, "topup", ac.saldo || 0)}><ArrowUp className="h-3 w-3" />Topup</Button>
                <Button size="sm" variant="outline" className="text-xs px-1" data-testid={`acc-deduct-${ac.id}`} onClick={() => openOp(ac.id, ac.label, "deduct", ac.saldo || 0)}>Kurangi</Button>
                <Button size="sm" variant="outline" className="text-xs px-1 gap-1" data-testid={`acc-set-${ac.id}`} onClick={() => openOp(ac.id, ac.label, "set", ac.saldo || 0)}><Pencil className="h-3 w-3" />Atur</Button>
                <Button size="sm" variant="outline" className="text-xs px-1" data-testid={`acc-edit-${ac.id}`} title="Ubah nama/nomor" onClick={() => startEdit(ac)}>Nama</Button>
                <Button size="sm" variant="outline" className="text-xs px-1 hover:bg-destructive hover:text-destructive-foreground" data-testid={`acc-del-${ac.id}`} onClick={() => delAccount(ac)}><Trash2 className="h-3 w-3" /></Button>
              </div>
              {renderOp(ac.id)}
            </div>
          ))}
        </div>

        {adding ? (
          <div className="rounded-lg border p-3 space-y-2.5" data-testid="account-form">
            <p className="text-sm font-medium">{editId ? "Edit Akun" : "Akun Baru"}</p>
            <div><Label>Nama Akun</Label><Input data-testid="acc-label" placeholder="mis. DANA Budi / BRI a.n Siti" value={label} onChange={(e) => setLabel(e.target.value)} /></div>
            <div><Label>Nomor / Info (opsional)</Label><Input data-testid="acc-number" value={accNo} onChange={(e) => setAccNo(e.target.value)} /></div>
            {!editId && <div><Label>Saldo Awal</Label><MoneyInput data-testid="acc-opening" value={opening} onChange={setOpening} /></div>}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => { setAdding(false); setEditId(null); }}>Batal</Button>
              <Button className="flex-1" data-testid="acc-save" disabled={busy} onClick={saveAccount}>Simpan</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="w-full gap-1.5" data-testid="btn-add-account" onClick={startAdd}><Plus className="h-4 w-4" />Tambah Akun</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
